import React, { useCallback, useState } from 'react';
import { filter } from 'lodash';
import map from 'lodash/map';
import {
  Button,
  Select,
  Title,
  MultiSelect,
  SimpleGrid,
  Alert,
  Flex,
  Modal,
  Text,
  LoadingOverlay,
  Loader,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { DatePickerInput } from '@mantine/dates';
import * as Sentry from '@sentry/browser';
import api, { apiService } from 'shared/services/api';
import ListHeading from 'shared/components/ListHeading';
import AppContent from 'shared/components/AppContent';
import useFetch from 'shared/hooks/useFetch';
import { useTranslations } from 'shared/translations/useTranslations';
import { ChartSection } from './ChartSection';
import { getActionsDataSet, getObjectsDataSet, getObservationsDataSet } from './utils';
import { StatisticsResponse } from './types';
import { toast } from 'react-toastify';

import './styles.scss';

const StatisticsV2 = () => {
  const translations = useTranslations();
  const date = new Date();
  const [isLoading, setLoading] = useState(false);
  const [isLoaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [data, setData] = useState<StatisticsResponse>({} as StatisticsResponse);
  const [refreshModalOpened, setRefreshModalOpened] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const dateFromDefault = new Date(date.getFullYear(), date.getMonth(), 1);
  const dateToDefault = new Date(date.getFullYear(), date.getMonth() + 1, 0);

  const form = useForm({
    initialValues: {
      placeId: '',
      dateFrom: dateFromDefault,
      dateTo: dateToDefault,
      deviceTypeId: undefined,
      deviceId: undefined,
      zoneId: undefined,
    },
    validate: {
      placeId: value => (!value ? 'Wybierz placówkę' : null),
    },
  });

  const { data: zonesData, refresh: loadZones } = useFetch<{ zones: any[] }>({
    initialValue: { zones: [] },
    shouldNotFetchAtStart: true,
    fetchAction: () => apiService({}).get(`/api/places/${form.values.placeId}/zones`),
  });

  const { data: placesData } = useFetch<any>({
    initialValue: [],
    fetchAction: () => api({}).get(`/places`),
  });

  const fetchStatistics = useCallback(
    async (placeId: string, dateFrom: Date, dateTo: Date) => {
      setLoading(true);
      setLoaded(false);
      setError(false);

      try {
        const from = new Date(dateFrom);
        from.setHours(0, 0, 0, 0);
        const to = new Date(dateTo);
        to.setHours(23, 59, 59, 59);

        const query = `from=${from.toISOString()}&to=${to.toISOString()}&placeId=${placeId}`;
        const { data: response } = await api({
          timeout: 240000,
          baseUrl: process.env.REACT_APP_BACKEND_STATISTICS_URL,
          // }).get(`/api/v2?${query}`);
        }).get(`/api/v3/place/${placeId}/statistics?${query}`);

        await loadZones();
        setData(response);
        setLoaded(true);
        form.setFieldValue('zoneId', undefined);
      } catch (err) {
        setError(true);
        Sentry.captureException(err);
      } finally {
        setLoading(false);
      }
    },
    [loadZones, form],
  );

  const handleSubmit = async (formValues: any) => {
    await fetchStatistics(formValues.placeId, formValues.dateFrom, formValues.dateTo);

    if (String(data.placeId) !== String(formValues.placeId)) {
      form.setFieldValue('deviceId', []);
      form.setFieldValue('deviceTypeId', undefined);
    }
  };

  const handleRefreshCache = useCallback(async () => {
    const placeId = form.values.placeId;
    if (!placeId) {
      toast.error('Wybierz placówkę przed odświeżeniem');
      return;
    }

    setIsRefreshing(true);
    setRefreshModalOpened(true);

    try {
      await api({
        timeout: 300000, // 5 minutes
        baseUrl: process.env.REACT_APP_BACKEND_STATISTICS_URL,
      }).post(`/api/v2/place/${placeId}/cache-inspections`);

      toast.success('Statystyki zostały odświeżone pomyślnie');
      setRefreshModalOpened(false);

      // Optionally refresh the statistics after cache refresh
      if (isLoaded && String(data.placeId) === String(placeId)) {
        await fetchStatistics(placeId, form.values.dateFrom, form.values.dateTo);
      }
    } catch (err) {
      toast.error('Wystąpił błąd podczas odświeżania statystyk');
      Sentry.captureException(err);
    } finally {
      setIsRefreshing(false);
    }
  }, [
    form.values.placeId,
    form.values.dateFrom,
    form.values.dateTo,
    isLoaded,
    data.placeId,
    fetchStatistics,
  ]);

  const filteredDevices = filter(data?.byDevice, device =>
    form.values.deviceTypeId ? device.data.deviceTypeId === form.values.deviceTypeId : true,
  );

  const getDeviceCleanName = (name: string) => {
    if (name.includes('_deleted')) {
      return name.replace('_deleted', ' (usunięte)');
    }
    return name;
  };

  const devicesOptions = map(filteredDevices, device => ({
    value: device.data?.id,
    label: getDeviceCleanName(device.data?.uniqueId),
  }));

  const onSelectAll = useCallback(() => {
    form.setFieldValue(
      'deviceId',
      map(devicesOptions, device => device.value),
    );
  }, [form, devicesOptions]);

  const placeData = data?.byPlace?.[data?.placeId];
  const zoneData = data?.byZone?.[form.values.zoneId];
  const deviceTypeData = data?.byDeviceType?.[form.values.deviceTypeId];

  return (
    <div>
      <ListHeading title={translations.global.statistics + ' v2'} />
      <AppContent>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <SimpleGrid cols={4} mb={15}>
            <div>
              <Select
                label={translations.global.facility}
                data={placesData?.map(place => ({
                  value: String(place.id),
                  label: place.name,
                }))}
                searchable
                required
                {...form.getInputProps('placeId')}
                error={form.errors.placeId}
              />
              {data.placeId && String(form.values.placeId) !== String(data.placeId) && (
                <Alert variant="outline" mt={15}>
                  {translations.statistics.searchAgain}
                </Alert>
              )}
            </div>
            <DatePickerInput
              label={translations.global.dateFrom}
              required
              {...form.getInputProps('dateFrom')}
            />
            <DatePickerInput
              label={translations.global.dateTo}
              required
              {...form.getInputProps('dateTo')}
            />
            <Flex direction="column" gap="sm" mt={25}>
              <Flex gap="sm">
                <Button type="submit" loading={isLoading}>
                  {translations.global.search}
                </Button>
                {form.values.placeId && (
                  <Button
                    variant="outline"
                    onClick={handleRefreshCache}
                    disabled={isRefreshing || isLoading}
                    loading={isRefreshing}>
                    {translations.global.refresh}
                  </Button>
                )}
              </Flex>
              {form.values.placeId && (
                <Text size="xs" color="dimmed" mt={5}>
                  Kliknij "Odśwież", aby zaktualizować dane inspekcji dla wybranej placówki. 
                  Proces może zająć kilka minut. Po zakończeniu odświeżania możesz ponownie wyszukać statystyki, 
                  aby zobaczyć zaktualizowane dane.
                </Text>
              )}
            </Flex>
          </SimpleGrid>

          {error && (
            <Alert variant="filled" color="red">
              {translations.global.somethingWentWrong}
            </Alert>
          )}

          {isLoaded && (
            <div>
              <Title mt={20} order={4} mb={0}>
                {translations.global.filters}
              </Title>
              <SimpleGrid cols={4} mb={15}>
                <Select
                  label={translations.zones.zone}
                  data={zonesData?.zones?.map(zone => ({ value: zone.id, label: zone.name }))}
                  clearable
                  {...form.getInputProps('zoneId')}
                />
                <Select
                  searchable
                  label={translations.global.deviceType}
                  data={map(data?.byDeviceType, device => ({
                    value: device.data?.id,
                    label: device.data?.name,
                  }))}
                  clearable
                  {...form.getInputProps('deviceTypeId')}
                />
                <Flex direction="column">
                  <MultiSelect
                    searchable
                    label={translations.global.device}
                    data={devicesOptions}
                    clearable
                    {...form.getInputProps('deviceId')}
                  />
                  <Button variant="outline" mt={10} onClick={onSelectAll}>
                    {translations.global.selectAll}
                  </Button>
                </Flex>
              </SimpleGrid>
            </div>
          )}
        </form>

        {isLoaded && placeData && (
          <div>
            <Title order={3} mt={25}>
              {translations.global.facility}
            </Title>
            <div className="row">
              <ChartSection
                title={translations.dashboard.equipmentMaintenance}
                data={placeData.dates}
                getDataSet={() => getActionsDataSet(placeData.dates, data.placeId)}
                translations={translations}
                legendId="legend-container-v2-place-actions"
              />
              <ChartSection
                title={translations.global.pestRelated}
                data={placeData.dates}
                getDataSet={() => getObjectsDataSet(placeData.dates, data.placeId)}
                translations={translations}
                legendId="legend-container-v2-place-objects"
              />
              <ChartSection
                title={translations.global.observations}
                data={placeData.dates}
                getDataSet={() => getObservationsDataSet(placeData.dates, data.placeId)}
                translations={translations}
                legendId="legend-container-v2-place-observations"
              />
            </div>

            {form.values?.zoneId && zoneData && (
              <div className="row">
                <Flex mt={50}>
                  <Title order={3} mt={25} mb={10}>
                    {translations.zones.zone}: {zoneData.data.name}
                  </Title>
                </Flex>
                <ChartSection
                  title={translations.dashboard.equipmentMaintenance}
                  data={zoneData.dates}
                  getDataSet={() => getActionsDataSet(zoneData.dates, form.values.zoneId)}
                  translations={translations}
                  legendId="legend-container-v2-zone-actions"
                />
                <ChartSection
                  title={translations.global.pestRelated}
                  data={zoneData.dates}
                  getDataSet={() => getObjectsDataSet(zoneData.dates, form.values.zoneId)}
                  translations={translations}
                  legendId="legend-container-v2-zone-objects"
                />
                <ChartSection
                  title={translations.global.observations}
                  data={zoneData.dates}
                  getDataSet={() => getObservationsDataSet(zoneData.dates, form.values.zoneId)}
                  translations={translations}
                  legendId="legend-container-v2-zone-observations"
                />
              </div>
            )}

            {form.values?.deviceTypeId && deviceTypeData && (
              <div className="row">
                <Flex mt={50}>
                  <Title order={3} mt={25} mb={10}>
                    {translations.global.deviceType}: {deviceTypeData.data.name}
                  </Title>
                </Flex>
                <ChartSection
                  title={translations.dashboard.equipmentMaintenance}
                  data={deviceTypeData.dates}
                  getDataSet={() =>
                    getActionsDataSet(deviceTypeData.dates, form.values.deviceTypeId)
                  }
                  translations={translations}
                  legendId="legend-container-v2-devicetype-actions"
                />
                <ChartSection
                  title={translations.global.pestRelated}
                  data={deviceTypeData.dates}
                  getDataSet={() =>
                    getObjectsDataSet(deviceTypeData.dates, form.values.deviceTypeId)
                  }
                  translations={translations}
                  legendId="legend-container-v2-devicetype-objects"
                />
                <ChartSection
                  title={translations.global.observations}
                  data={deviceTypeData.dates}
                  getDataSet={() =>
                    getObservationsDataSet(deviceTypeData.dates, form.values.deviceTypeId)
                  }
                  translations={translations}
                  legendId="legend-container-v2-devicetype-observations"
                />
              </div>
            )}

            {form.values?.deviceId?.length > 0 && (
              <div>
                {form.values.deviceId.map(deviceId => {
                  const deviceData = data?.byDevice?.[deviceId];
                  if (!deviceData) return null;

                  return (
                    <div className="row" key={deviceId}>
                      <Flex>
                        <Title order={3} mt={25}>
                          {translations.global.device} {deviceData.data.uniqueId}
                        </Title>
                      </Flex>
                      <ChartSection
                        title={translations.dashboard.equipmentMaintenance}
                        data={deviceData.dates}
                        getDataSet={() => getActionsDataSet(deviceData.dates, deviceId)}
                        translations={translations}
                        legendId={`legend-container-v2-device-${deviceId}-actions`}
                      />
                      <ChartSection
                        title={translations.global.pestRelated}
                        data={deviceData.dates}
                        getDataSet={() => getObjectsDataSet(deviceData.dates, deviceId)}
                        translations={translations}
                        legendId={`legend-container-v2-device-${deviceId}-objects`}
                      />
                      <ChartSection
                        title={translations.global.observations}
                        data={deviceData.dates}
                        getDataSet={() => getObservationsDataSet(deviceData.dates, deviceId)}
                        translations={translations}
                        legendId={`legend-container-v2-device-${deviceId}-observations`}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </AppContent>

      <Modal
        opened={refreshModalOpened}
        onClose={() => !isRefreshing && setRefreshModalOpened(false)}
        title="Odświeżanie statystyk"
        centered
        closeOnClickOutside={false}
        closeOnEscape={false}
        withCloseButton={!isRefreshing}>
        <div
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <Text size="sm" mb="md" style={{ textAlign: 'center' }}>
            Odświeżanie danych inspekcji dla wybranej placówki może zająć kilka minut. 
            Proszę nie zamykać tego okna.
          </Text>
          <Text size="xs" color="dimmed" mb="md" style={{ textAlign: 'center' }}>
            Po zakończeniu odświeżania możesz ponownie wyszukać statystyki, aby zobaczyć zaktualizowane dane.
          </Text>
          <Loader size="lg" mb={5} mt={5} />
          {isRefreshing && (
            <Text size="xs" color="dimmed">
              Proces w toku...
            </Text>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default StatisticsV2;
